import { store } from '@/state/store';
import { NumberFormatter } from '@internationalized/number';
import BigNumber from 'bignumber.js';
import { format, isSameDay, parseISO } from 'date-fns';
import { format as dateFnsTzFormat, toZonedTime } from 'date-fns-tz';
import { DateFormatter } from 'react-aria';
import { qtyFormatterConfig } from '../config/config';

export const formatNotification = (date: Date) => {
    return format(date, 'HH:mm:ss');
};

export const formatUTCNotification = (date: Date, tz = 'UTC') => {
    const showMilliseconds = store.getState().settings.general.showMilliseconds.value;
    return dateFnsTzFormat(toZonedTime(date, tz), `HH:mm:ss${showMilliseconds ? ':SSS' : ''}`, { timeZone: tz });
};

export const formatLocalTimestamp = (ms: number) => {
    // return dateFnsTzFormat(toZonedTime(ms, tz), 'HH:mm:ss.SSS', { timeZone: tz });
    const showMilliseconds = store.getState().settings.general.showMilliseconds.value;
    return format(ms, `HH:mm:ss${showMilliseconds ? ':SSS' : ''}`);
};

export const formatTimestamp = (ms: number, tz = 'UTC') => {
    // return dateFnsTzFormat(toZonedTime(ms, tz), 'HH:mm:ss.SSS', { timeZone: tz });
    const showMilliseconds = store.getState().settings.general.showMilliseconds.value;

    return dateFnsTzFormat(toZonedTime(ms, tz), `HH:mm:ss${showMilliseconds ? ':SSS' : ''}`, { timeZone: tz });
};

export const formatDateTime = (formatDate: DateFormatter, datetime: number) => {
    return `${formatDate.format(new Date(datetime))} ${formatTimestamp(datetime)}`;
};

export const formatLocalDateTime = (formatDate: DateFormatter, datetime: number) => {
    return `${formatDate.format(new Date(datetime))} ${formatLocalTimestamp(datetime)}`;
};

export const formatPlural = (str: string, num: number) => str + (num > 1 ? 's' : '');

export const formatNumber = (quantity, hundreds, thousands, millions) => {
    if (quantity < 100_000) {
        return hundreds.format(quantity);
    } else if (quantity >= 100_000 && quantity < 1_000_000) {
        const value = BigNumber(quantity).minus(500).toNumber();
        if (value < 100_000) return thousands.format(quantity);
        return thousands.format(value);
    } else if (quantity >= 1_000_000) {
        const value = BigNumber(quantity).minus(50_000).toNumber();
        if (value < 1_000_000) return millions.format(quantity);
        return millions.format(value);
    }
};

export const formatTraderNumber = (quantity, hundreds, thousands, millions) => {
    if (quantity < 1_000) {
        return hundreds.format(quantity);
    } else if (quantity >= 1_000 && quantity < 100_000) {
        const value = BigNumber(quantity).minus(5).toNumber();
        if (value < 1_000) return thousands.format(quantity);
        return thousands.format(value);
    } else if (quantity >= 100_000 && quantity < 1_000_000) {
        const value = BigNumber(quantity).minus(500).toNumber();
        if (value < 100_000) return thousands.format(quantity);
        return thousands.format(value);
    } else if (quantity >= 1_000_000) {
        const value = BigNumber(quantity).minus(50_000).toNumber();
        if (value < 1_000_000) return millions.format(quantity);
        return millions.format(value);
    }
};

export const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
export const capitalizeAll = (str: string) => {
    return str
        .split(/[_\s]+/) // Split by underscore or space (one or more)
        .filter(Boolean) // Remove empty entries
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export function addSpacingToCharacter(value: string, position: number) {
    if (value === null || value === undefined) return '';

    return value
        .replace(/\s+/g, '')
        .replace(new RegExp(`.{1,${position}}`, 'g'), '$& ')
        .trim();
}

export function addDashesToCharacter(value: string) {
    if (value === null || value === undefined) return '';

    return value
        .replace(/-/g, '')
        .replace(/\s+/g, '')
        .replace(/.{2}(?=.)/g, '$&-')
        .trim();
}

export const getQtyFormatter = () => {
    /* 
        React aria exposes their number formatter in hook matter, which can be a limitation.
        Use this function when hook is not an option to format the numbers.
        This implementation is an abridged version of react-aria's getDefaultLocale and useNumberFormatter.
        Please lookup in node_modules if updates are needed.
    */
    let locale = (typeof navigator !== 'undefined' && navigator.language) || 'en-US';
    try {
        Intl.DateTimeFormat.supportedLocalesOf([locale]);
    } catch (_err) {
        locale = 'en-US';
    }

    return new NumberFormatter(locale, qtyFormatterConfig);
};

export const getDateFormat = (
    locale: string,
    options?: {
        year?: 'numeric' | '2-digit';
        month?: 'numeric' | '2-digit';
        day?: 'numeric' | '2-digit';
    }
): string => {
    const date = new Date();
    const dateFormatter = new Intl.DateTimeFormat(locale, {
        year: options?.year || 'numeric',
        month: options?.month || '2-digit',
        day: options?.day || '2-digit'
    });

    const parts = dateFormatter.formatToParts(date);
    const dateFormatPattern = parts
        .map((part) => {
            switch (part.type) {
                case 'literal':
                    return part.value;
                case 'year':
                    return 'yyyy';
                case 'month':
                    return 'MM';
                case 'day':
                    return 'dd';
                default:
                    return part.value;
            }
        })
        .join('');

    return dateFormatPattern;
};

export function isSameUTCDay(givenDateISO) {
    const currentUtcDate = new Date();
    const givenDate = parseISO(givenDateISO);

    // Convert both dates to UTC for comparison
    const currentUtc = toZonedTime(currentUtcDate, 'UTC');
    // Use `isSameDay` to check if they fall on the same day
    return isSameDay(currentUtc, givenDate);
}
